import { getLiveStreamUrls, toTimestamp } from "./common";

/**
 * Pure evaluator for an event's admin configuration.
 *
 * Given an event object (the in-progress editor state OR a fetched event), it
 * returns a structured readiness report grouped by feature area. It is the
 * single source of truth for "what a well-configured event looks like" and is
 * safe to call on every render (pure, no side effects).
 *
 * Severities: "blocker" (feature won't work) | "warning" (works but probably
 * unintended) | "info" (FYI, not a problem).
 * Area status: "blocker" | "warning" | "ok" | "off" (feature not used).
 *
 * @param {object|null} event
 * @returns {{ areas: Array, summary: { blockers: number, warnings: number } }}
 */
export function evaluateEventConfig(event) {
    if (!event) {
        return { areas: [], summary: { blockers: 0, warnings: 0 } };
    }

    const now = Date.now();
    const str = (v) => (typeof v === "string" ? v.trim() : v ? String(v) : "");

    const isEnded = event.isEnded === true;

    const name = str(event.name);
    const coverImage = str(event.coverImage);
    const dateMs = msOf(event.dateTime);
    const status = event.status;

    const registerMs = msOf(event.registerExpiredDateTime);
    const allowedEntries = Array.isArray(event.allowedEntries)
        ? event.allowedEntries
        : [];

    const price = parseFloat(event.liveStreamTicketPrice) || 0;
    const purchaseMs = msOf(event.liveStreamTicketPurchaseDeadline);
    const streamUrls = getLiveStreamUrls(event.liveStreamUrl);

    const venue = str(event.venue);
    const googleMap = str(event.googleMap);
    const waze = str(event.waze);

    // --- Listing & Discovery ---
    const listing = [];
    if (!dateMs) {
        listing.push(
            finding(
                "blocker",
                "No event date set",
                "The event won't appear on the public listing (it falls into neither Upcoming nor Past)."
            )
        );
    }
    if (!name) {
        listing.push(
            finding(
                "warning",
                "No event name",
                "The title shows blank on the event and checkout pages."
            )
        );
    }
    if (!coverImage) {
        listing.push(
            finding(
                "warning",
                "No cover image",
                "The listing card and event page show a broken image."
            )
        );
    }
    if (status !== "A") {
        listing.push(
            finding(
                "warning",
                "Status is Inactive",
                "Hidden from the public listing — still reachable by direct link. Flip to Active to publish."
            )
        );
    }

    // --- Registration ---
    const registration = [];
    if (!registerMs) {
        registration.push(
            finding(
                "warning",
                "No registration deadline",
                "Fighter registration stays open indefinitely (only ending the event closes it)."
            )
        );
    } else if (registerMs < now && !isEnded) {
        registration.push(
            finding(
                "info",
                "Registration deadline has passed",
                "The 'Register' button is closed for fighters."
            )
        );
    }
    if (allowedEntries.length === 0) {
        registration.push(
            finding(
                "info",
                "All disciplines allowed",
                "No entry restriction set — fighters can register for any discipline."
            )
        );
    }

    // --- Live Stream Ticketing ---
    const ticketing = [];
    const ticketingEnabled =
        price > 0 || !!purchaseMs || streamUrls.length > 0;
    if (ticketingEnabled) {
        if (price <= 0) {
            ticketing.push(
                finding(
                    "blocker",
                    "No ticket price set",
                    "Live stream tickets can't be sold (a zero price is rejected at checkout)."
                )
            );
        }
        if (!purchaseMs) {
            ticketing.push(
                finding(
                    "blocker",
                    "No purchase deadline set",
                    "Tickets can't be sold — a missing deadline is treated as 'not on sale'."
                )
            );
        } else if (purchaseMs < now && !isEnded) {
            ticketing.push(
                finding(
                    "warning",
                    "Purchase deadline has passed",
                    "Ticket sales are closed. Existing ticket-holders can still watch."
                )
            );
        }
        if (streamUrls.length === 0) {
            ticketing.push(
                finding(
                    "blocker",
                    "No live stream URL set",
                    "Buyers can pay but won't be able to watch — there's no stream to show."
                )
            );
        }
    }

    // --- Location ---
    const location = [];
    if (!venue) {
        location.push(
            finding(
                "warning",
                "No venue set",
                "The event page shows '?' instead of a venue name."
            )
        );
    }
    if (venue && !googleMap && !waze) {
        location.push(
            finding(
                "info",
                "No map links",
                "The Google Maps / Waze buttons are shown but lead nowhere."
            )
        );
    }

    const areas = [
        area("listing", "Listing & Discovery", listing, true),
        area("registration", "Registration", registration, true),
        area("ticketing", "Live Stream Ticketing", ticketing, ticketingEnabled),
        area("location", "Location", location, true),
    ];

    let blockers = 0;
    let warnings = 0;
    areas.forEach((a) =>
        a.findings.forEach((fd) => {
            if (fd.severity === "blocker") blockers += 1;
            else if (fd.severity === "warning") warnings += 1;
        })
    );

    return { areas, summary: { blockers, warnings } };
}

function msOf(value) {
    const ts = toTimestamp(value);
    return ts ? ts.toMillis() : null;
}

function finding(severity, title, consequence) {
    return { severity, title, consequence };
}

function area(key, label, findings, enabled) {
    let status;
    if (!enabled) status = "off";
    else if (findings.some((x) => x.severity === "blocker")) status = "blocker";
    else if (findings.some((x) => x.severity === "warning")) status = "warning";
    else status = "ok";
    return { key, label, findings, status };
}
